import React from 'react'

import InfiniteList, { handleFetchMore } from 'components/InfiniteList'
import { getShareThumbnail } from 'modules/shares/utils'

import {
  UserPermissionsTabContent,
  buildPlaceholderArray,
  EmptyContent,
} from './UserPermissionsContent'
import { useManageUserSharePermissions } from './useManageUserPermissions'
import { UserPermissionRow } from 'components/ListRow'

import { ReactComponent as ImagePlaceholder } from '@sketch/icons/image-placeholder-64-svg'
import { MemberPermissionsActions } from 'components/AccessLevelSelect/variants/ManageSharePermissionsAccessSelect'

const PAGE_SIZE = 10

type SharePermissionsTabProps = {
  workspaceIdentifier: string
  workspaceMembershipIdentifier: string
  rows: number
}

export const SharePermissionsTab = ({
  workspaceIdentifier,
  workspaceMembershipIdentifier,
  rows,
}: SharePermissionsTabProps) => {
  const shares = useManageUserSharePermissions({
    workspaceIdentifier,
    workspaceMembershipIdentifier,
  })

  const total = shares.total
  const sharesLength = shares?.data?.length || 0
  const placeholderMembers = (total || PAGE_SIZE) - sharesLength

  if (shares.loading && sharesLength === 0) {
    return (
      <UserPermissionsTabContent rows={rows} onSearch={shares.setShareSearch}>
        {buildPlaceholderArray(2)}
      </UserPermissionsTabContent>
    )
  }

  if (!shares.loading && total === 0) {
    return (
      <UserPermissionsTabContent rows={rows} onSearch={shares.setShareSearch}>
        {shares.isUsingFilter ? (
          <EmptyContent
            title="No search results"
            subtitle="No document names matched your search."
          />
        ) : (
          <EmptyContent
            title="No documents"
            subtitle="This person hasn’t been added to any documents yet."
          />
        )}
      </UserPermissionsTabContent>
    )
  }

  return (
    <UserPermissionsTabContent rows={rows} onSearch={shares.setShareSearch}>
      <InfiniteList
        renderLoading={() => null}
        placeholderItems={buildPlaceholderArray(
          Math.min(PAGE_SIZE, placeholderMembers)
        )}
        onLoadMore={handleFetchMore(shares.fetchMore, shares.path, {
          after: shares.after,
        })}
        canLoadMore={shares.after !== null}
        data-testid="shares-permissions-tab"
      >
        {shares.data?.map(membership => (
          <UserPermissionRow
            key={membership.identifier}
            name={membership.share?.name || ''}
            avatarSrc={membership?.share && getShareThumbnail(membership.share)}
            icon={ImagePlaceholder}
            actions={
              <MemberPermissionsActions
                member={membership}
                share={membership.share!}
                workspaceIdentifier={workspaceIdentifier}
                workspaceMembershipIdentifier={workspaceMembershipIdentifier}
              />
            }
          />
        ))}
      </InfiniteList>
    </UserPermissionsTabContent>
  )
}
